import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { useStore } from '@nanostores/react'
import { SparklesIcon } from 'lucide-react'

import type { BillingInterval, CatalogPlan, PaidPlanId } from '~/features/billing/types'

import { $user } from '~/features/auth/stores/user'
import { usePlatformSettings } from '~/features/auth/hooks/useAuthQueries'
import {
  useCheckoutMutation,
  useCheckoutReturn,
  useCurrentWorkspacePlan,
  useSubscriptionMutation
} from '~/features/billing/hooks/useBillingHooks'
import { PLAN_ORDER } from '~/features/billing/constants'
import { $currentInterval } from '~/features/billing/stores/plans'
import { $checkoutSyncing } from '~/features/billing/stores/blocked'
import { formatDate, formatUsd, isPaidPlan } from '~/features/billing/utils'
import { useCurrentWorkspaceQuery } from '~/features/workspaces/hooks/useWorkspaceQueries'
import { Message } from '~/elements/Message'
import { CustomPlanInquiryModal } from '~/components/billing/CustomPlanInquiryModal'
import { ConfirmBillingDialog } from '~/components/billing/ConfirmBillingDialog'
import { PlanCard, PlanCardSkeleton } from '~/components/billing/PlanCard.tsx'
import type { PlanCardAction } from '~/components/billing/PlanCard.tsx'

const intervalLabel = (interval: BillingInterval) => (interval === 'year' ? 'yearly' : 'monthly')

type Pending =
  | { kind: 'change'; plan: CatalogPlan & { id: PaidPlanId }; interval: BillingInterval }
  | { kind: 'cancel' }
  | null

export function Plans({ intendedPlan }: { intendedPlan?: string } = {}) {
  const interval = useStore($currentInterval)
  const syncing = useStore($checkoutSyncing)
  const currentUser = useStore($user)
  const { data: settings, isPending: settingsPending } = usePlatformSettings()
  const { data: workspace } = useCurrentWorkspaceQuery()
  const { planId: currentPlanId, state, catalog, loading } = useCurrentWorkspacePlan()
  const checkout = useCheckoutMutation()
  useCheckoutReturn()
  const subscription = useSubscriptionMutation()

  const [pending, setPending] = useState<Pending>(null)
  const [inquiryOpen, setInquiryOpen] = useState(false)
  const [checkoutPlan, setCheckoutPlan] = useState<PaidPlanId>()

  const showSkeleton = settingsPending || (settings?.selfHosted === false && (loading || !catalog))
  const plans = PLAN_ORDER.map((id) => catalog?.plans.find((plan) => plan.id === id)).filter(
    (plan): plan is CatalogPlan => Boolean(plan)
  )

  const currentInterval = state?.interval ?? null
  const hasSubscription = Boolean(state?.paddle?.subscriptionId) && currentPlanId !== 'free'
  const cancelAt = state?.scheduledChange?.action === 'cancel' ? state.scheduledChange.effectiveAt : null
  const currentRank = PLAN_ORDER.indexOf(currentPlanId)
  const recommendedId =
    intendedPlan ?? plans.find((plan) => isPaidPlan(plan.id) && PLAN_ORDER.indexOf(plan.id) > currentRank)?.id

  useEffect(() => {
    if (intendedPlan) {
      document
        .getElementById(`billing-plan-${intendedPlan}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [intendedPlan, showSkeleton])

  const startCheckout = (plan: PaidPlanId) => {
    setCheckoutPlan(plan)
    checkout.mutate({ plan, interval }, { onSettled: () => setCheckoutPlan(undefined) })
  }

  const actionFor = (plan: CatalogPlan): { action: PlanCardAction; note?: ReactNode } => {
    const isCurrentPlan = plan.id === currentPlanId

    if (plan.id === 'enterprise') {
      return isCurrentPlan ?
          { action: { label: 'Current plan', disabled: true, variant: 'secondary' } }
        : { action: { label: 'Talk to us', onClick: () => setInquiryOpen(true) } }
    }

    if (currentPlanId === 'enterprise') {
      return { action: { label: 'Contact us', onClick: () => setInquiryOpen(true), variant: 'secondary' } }
    }

    if (plan.id === 'free') {
      if (isCurrentPlan) return { action: { label: 'Current plan', disabled: true, variant: 'secondary' } }
      if (cancelAt) {
        return {
          action: { label: `Switches on ${formatDate(cancelAt)}`, disabled: true, variant: 'secondary' }
        }
      }
      return {
        action: {
          label: 'Downgrade to Free',
          onClick: () => setPending({ kind: 'cancel' }),
          disabled: !hasSubscription,
          variant: 'secondary'
        }
      }
    }

    const paidPlan = plan as CatalogPlan & { id: PaidPlanId }

    if (isCurrentPlan && currentInterval === interval) {
      return cancelAt ?
          {
            action: {
              label: 'Resume subscription',
              loading: subscription.isPending,
              onClick: () => subscription.mutate({ action: 'resume' })
            },
            note: `Cancels on ${formatDate(cancelAt)}`
          }
        : { action: { label: 'Current plan', disabled: true, variant: 'secondary' } }
    }

    if (hasSubscription) {
      return {
        action: {
          label:
            isCurrentPlan ?
              `Switch to ${intervalLabel(interval)} billing`
            : `Switch to ${plan.name} ${intervalLabel(interval)}`,
          onClick: () => setPending({ kind: 'change', plan: paidPlan, interval }),
          variant: PLAN_ORDER.indexOf(plan.id) > currentRank ? 'primary' : 'secondary'
        }
      }
    }

    return {
      action: {
        label: (
          <>
            Upgrade to {plan.name}
            <SparklesIcon />
          </>
        ),
        loading: checkoutPlan === plan.id && checkout.isPending,
        disabled: syncing || (checkout.isPending && checkoutPlan !== plan.id),
        onClick: () => startCheckout(paidPlan.id)
      }
    }
  }

  const gridCols = 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4'

  return (
    <>
      {syncing && (
        <Message size="small" variant="info">
          Payment received. Activating your new plan…
        </Message>
      )}

      <div className={`grid gap-5 ${gridCols}`}>
        {showSkeleton ?
          Array.from({ length: 4 }).map((_, index) => <PlanCardSkeleton key={index} />)
        : plans.map((plan) => {
            const { action, note } = actionFor(plan)
            const current =
              plan.id === currentPlanId && (!isPaidPlan(plan.id) || currentInterval === interval)
            return (
              <PlanCard
                action={action}
                current={current}
                id={`billing-plan-${plan.id}`}
                interval={interval}
                key={plan.id}
                note={note}
                plan={plan}
                recommended={!current && plan.id === recommendedId}
              />
            )
          })
        }
      </div>

      {catalog && (
        <p className="text-xs text-content2">
          Prices are in USD and exclude VAT and sales tax, which Paddle adds at checkout where it applies.
          Businesses with a valid VAT ID are not charged VAT.
        </p>
      )}

      {!showSkeleton && !catalog && settings?.selfHosted === false && (
        <Message size="small">Plans are unavailable right now. Please refresh in a moment.</Message>
      )}

      {pending?.kind === 'change' && (
        <ConfirmBillingDialog
          confirmLabel={`Switch to ${pending.plan.name}`}
          description={
            <>
              <p>
                Your subscription moves to{' '}
                <span className="font-medium text-content">
                  {pending.plan.name}, billed {intervalLabel(pending.interval)}
                </span>
                {pending.plan.price ?
                  ` at ${formatUsd(pending.plan.price[pending.interval])}/${pending.interval}`
                : null}
                , right away.
              </p>
              <p>
                The change is prorated immediately: you are charged, or credited, the difference for the rest
                of the current billing period. Extra seats and projects carry over at the new plan's prices.
              </p>
            </>
          }
          loading={subscription.isPending || checkout.isPending}
          onConfirm={() =>
            subscription.mutateAsync({
              action: 'change_plan',
              plan: pending.plan.id,
              interval: pending.interval
            })
          }
          onOpenChange={(open) => !open && setPending(null)}
          open
          title={`Switch to ${pending.plan.name}?`}
        />
      )}

      {pending?.kind === 'cancel' && (
        <ConfirmBillingDialog
          confirmLabel="Cancel subscription"
          danger
          description={
            <>
              <p>
                Your subscription stays active until the end of the current billing period. After that the
                workspace moves to the Free plan and its limits.
              </p>
              <p>You can resume the subscription any time before it ends.</p>
            </>
          }
          loading={subscription.isPending}
          onConfirm={() => subscription.mutateAsync({ action: 'cancel' })}
          onOpenChange={(open) => !open && setPending(null)}
          open
          title="Cancel your subscription?"
        />
      )}

      <CustomPlanInquiryModal
        currentPlan={currentPlanId}
        defaultEmail={currentUser.login}
        onOpenChange={setInquiryOpen}
        open={inquiryOpen}
        workspaceName={workspace?.name}
      />
    </>
  )
}
